import _find from "lodash/find";
import * as Paragraph from "./paragraph";

// Turn a section's stored `values` into what its template reads.
//
// For most param types the stored value IS the rendered value and this is the
// identity. It exists for the two that are not: an `object` param's value is
// keyed by the params of ANOTHER type, which has to be looked up by name, and a
// `list` param's value is an array of whatever its item config describes. Both
// recurse.
//
// And it exists for a third that is neither: a `paragraph` param stores a Quill
// delta, which is not what a template wants to interpolate. This is where it
// becomes HTML, which is where the archived project did it too — so a template
// written against gen-2 reads `<%-content%>` and gets markup, unchanged.
//
// `markdown` was the archived project's other conversion here. That param type
// is still not ported — no widget authors it — so neither is its conversion.
//
// An `image` param is deliberately NOT a fourth case, though it is the one that
// looks like it should be. Its value is `{ url, description }` and a template
// reads the two separately —
// `<img src="<%= hero.url %>" alt="<%= hero.description %>">` — which is what
// gen-2's templates were written against. Emitting the tag here instead would
// decide the alt text, the sizing and the class list on the template's behalf,
// and a section's markup is exactly the thing this service lets a tenant own.
// It is also why the URL the picker stores is relative: EJS escaping is the
// template's business, and a URL is a URL wherever the page is served from.
//
// BOUNDED, and the bound is the point of the `depth` argument — P181.
//
// Both recursions below are driven by the VALUES and not by the declaration. An
// `object` param names its target type by name, so a type whose param points
// back at its own type is a legal declaration and a cycle: the walk then goes
// as deep as the stored value nests and no deeper, which is a limit an author
// sets by typing. `PageElement.config` and a section's `values` are `JSON` on
// the wire and opaque in the table — @common/schema's validation only reaches
// the top level of a union body, see api/code/src/validate — so nothing between
// the browser and here counts the nesting.
//
// HOW DEEP IT CAN ACTUALLY GET, because the row this closes overstated it and
// the correction is the reason for the number chosen. `resolveValues` overflows
// the stack somewhere between 2 000 and 4 000 levels — a 36 KB payload, which
// V8's JSON parser takes without complaint because it is iterative. What cannot
// reach that is the stored document: DYNAMODB REFUSES AN ITEM NESTED MORE THAN
// 32 LEVELS, measured against this service's own tables, and `values`
// sits four levels into a page row, so the deepest `values` a page can hold is
// 28. The composer on the render path and the composer in the editor both read
// documents that came through that ceiling, and `assertValidResolved` does not
// recurse into a union body at all — verified, 10 000 levels pass through it
// untouched — so the write is refused by DynamoDB rather than by anything that
// stack-overflows on the way.
//
// So this is a bound on a walk that is unbounded in the code and bounded in
// practice by a storage limit nothing here states or checks. 32 is the number
// the template engine took in P165 for expressions, and it is above the 28 a
// page row can carry — which is the property worth keeping if the store ever
// changes: no document that can be saved today is truncated by this.
//
// Reaching the cap returns the raw `values`, exactly as an object param whose
// target type no longer exists does, and for the reason given there: the
// template gets something shaped roughly right rather than an exception. A
// throw would be no better than the RangeError it is there to prevent —
// `resolveValues` is called to build an ARGUMENT to Template.render in
// ./elements, outside the try/catch that turns a bad template into content, so
// it would take the whole document down.
//
// `curr` is a param declaration (or the synthetic root, `{ type: "object",
// config: { type: "root" } }`); `types` is the whole array, because an `object`
// param names its target rather than nesting it.
const MAX_DEPTH = 32;

export const resolveValues = (curr, values, types, depth = 0) => {
  if (!curr || !values) {
    return values;
  }

  if (depth > MAX_DEPTH) {
    return values;
  }

  const { type, config } = curr;

  if (type === "object") {
    const target = _find(types, { name: config.type });

    // A param pointing at a type that no longer exists. Returning the raw
    // values is what the archived project did and is the useful answer: the
    // template still gets something shaped roughly right, rather than
    // undefined, which would read as "the author entered nothing".
    if (!target) {
      return values;
    }

    const res = {};
    for (const param of target.params) {
      res[param.name] = resolveValues(param, values[param.name], types, depth + 1);
    }

    return res;
  }

  if (type === "paragraph") {
    return Paragraph.render(values);
  }

  if (type === "list") {
    return Array.isArray(values)
      ? values.map((value) => resolveValues(config, value, types, depth + 1))
      : values;
  }

  return values;
};

// The entry point every consumer uses, and the reason "root" is hard-coded
// across the schema, the model's seed and the values form.
export const resolveSectionValues = ({ types, values }) =>
  resolveValues({ type: "object", config: { type: "root" } }, values || {}, types || []);
